/**
 * LeatherMirrorController — the Mirror sub-tool for leather objects (#973,
 * epic #781). Select a part, cutout or seam, hover an axis on any visible
 * outline, and click to commit a standalone mirrored duplicate.
 *
 * A tool OWNS a controller instance and DRIVES it by composition (it is NOT a
 * base class), the arrangement `MirrorController` / `OutlineOffsetController` /
 * `RotationController` already use. What the owning tool injects through
 * `LeatherMirrorHost` is the editor plumbing; the gesture itself lives here.
 *
 * Deliberately shared with the construction Mirror sub-tool (#180) rather than
 * re-derived, since a forked copy is exactly what #990 records the cost of:
 *   - the axis scan and its ref→reflection resolution are `mirrorAxis.ts`,
 *     extracted from `MirrorController` for this ticket;
 *   - the reflection itself is `mirrorPieceAcross` / `mirrorCutoutAcross` /
 *     `mirrorStitchLineAcross`, which express an arbitrary-axis mirror using
 *     the axis-aligned flips the Flip commands already used;
 *   - the deep clone with fresh ids is `clonePieceForDuplicate` and friends,
 *     shared with Duplicate (#907).
 *
 * Three deliberate differences from the construction gesture:
 *
 *  1. AXES COME FROM EVERYTHING VISIBLE — construction lines, part outlines and
 *     cutout outlines alike (the maintainer's call on #973). Mirroring a part
 *     across a construction centre line is the classic workflow; mirroring it
 *     across another part's edge is just as useful.
 *
 *  2. AN OBJECT MAY BE MIRRORED ACROSS ITS OWN EDGE. Construction excludes the
 *     selected sources from axis candidacy on the grounds that reflecting a
 *     shape across itself is a near-no-op. That is true of a pivot inside the
 *     shape, but reflecting a part across its own straight EDGE is the everyday
 *     leather move — it produces the adjacent, opposite-hand half of a folded
 *     piece — so leather keeps it.
 *
 *  3. THE COPY IS ALWAYS STANDALONE. #973 asks for simple-mirror parity, so
 *     there is no live re-deriving link to maintain: the reflected geometry is
 *     baked once, exactly like Duplicate.
 */
import paper from "paper";
import type { Scene } from "../canvas/Scene";
import {
  isVisible,
  nextCutoutName,
  nextPartName,
  nextStitchName,
  pieceStitchLines,
  projectConstructionLines,
  type Piece,
  type Project,
} from "../model/Project";
import {
  applyMirror,
  buildMirrorMatrix,
  mirrorPathData,
  type AxisSpec,
} from "../model/constructionMirror";
import {
  findAxisCandidate,
  axisDistanceMm,
  selfAxisGap,
  GAP_REDRAW_EPS_MM,
  nearestAxisFeature,
  resolveAxisSpecFrom,
  type AxisOutline,
  type AxisRef,
} from "../model/mirrorAxis";
import {
  mirrorCutoutAcross,
  mirrorPieceAcross,
  mirrorStitchLineAcross,
  pieceDisplayOffset,
  translatePathData,
  translatePiece,
} from "../model/pieceTransform";
import { seamPathData } from "../model/seamGeometry";
import {
  clonePieceForDuplicate,
  cloneCutoutForDuplicate,
  cloneStitchForDuplicate,
  mapPiece,
} from "../model/pieceDuplicate";
import { cssVar } from "../colors";
import { pickLeatherObjectAt, selectionForPick } from "./leatherPick";
import { showCanvasToast } from "../canvasToast";
import { hasHover, type PointerKind } from "../canvas/pointerPrecision";

/** Shown when a click on bare mat teaches the user nothing — no object picked,
 *  no axis lit up, nothing selected to mirror in the first place. */
const NO_SELECTION_HINT = "Select a part, cutout or seam to mirror";

/** Highlight / preview stroke. The leather accent pair, as the rest of the
 *  leather overlays use — read through a function so a theme switch is picked
 *  up rather than baked in at module load. */
const PREVIEW_STROKE = () => cssVar("--canvas-accent");

/** What the current selection resolves to as a mirror source. Child scopes are
 *  detected BEFORE the piece scope, because a child selection also carries its
 *  owner's `pieceId` (the same precedence `duplicateChildFeatureSelection`
 *  uses). */
export type MirrorSelection =
  | { kind: "pieces"; pieceIds: string[] }
  | { kind: "cutout"; pieceId: string; cutoutId: string }
  | { kind: "stitch"; entries: Array<{ pieceId: string; stitchId: string }> }
  | null;

export interface LeatherMirrorHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(sel: Parameters<Scene["setSelection"]>[0]): void;
  /** Switch the active editor tool by id — used to hand the user back to
   *  Select once a mirror has landed. */
  activateTool(id: string): void;
}

/** Resolve the scene selection into the objects Mirror will reflect. */
export function mirrorSelection(scene: Scene): MirrorSelection {
  const sel = scene.getSelection();
  const stitchIds = sel.stitchIds ?? (sel.stitchId ? [sel.stitchId] : []);
  if (stitchIds.length > 0 && sel.pieceId) {
    return {
      kind: "stitch",
      entries: stitchIds.map((stitchId) => ({
        pieceId: sel.pieceId as string,
        stitchId,
      })),
    };
  }
  if (sel.cutoutId && sel.pieceId) {
    return { kind: "cutout", pieceId: sel.pieceId, cutoutId: sel.cutoutId };
  }
  const pieceIds = sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []);
  if (pieceIds.length > 0) return { kind: "pieces", pieceIds: [...pieceIds] };
  return null;
}

/**
 * Every outline that can supply a mirror axis: visible construction lines,
 * visible part outlines, visible cutout outlines. Keys are namespaced so a
 * resolved ref can be traced back to the geometry it addresses.
 *
 * Unlike the Offset gesture's source list, construction-LINKED parts are
 * included — an axis is only read, never followed, so a re-deriving outline is
 * a perfectly good thing to reflect across.
 */
export function mirrorAxisOutlines(
  project: Project,
  partLayoutView: "spread" | "assembled",
  scope: paper.PaperScope,
): AxisOutline[] {
  const out: AxisOutline[] = [];
  for (const line of projectConstructionLines(project)) {
    if (line.visible === false) continue;
    out.push({ key: `clin:${line.id}`, pathData: line.pathData });
  }
  for (const piece of project.pieces) {
    if (!isVisible(piece)) continue;
    // A LINKED part is drawn at its cut-layout offset, which is not baked into
    // the stored geometry (#786) — so in Spread view the outline the user is
    // pointing at is `off` away from `shapePathData`. Offer axes where they are
    // actually DRAWN; the commit converts back per source (see `commitFromHover`).
    const off = pieceDisplayOffset(piece, partLayoutView);
    const shift = (d: string) =>
      off.dx || off.dy ? translatePathData(d, off.dx, off.dy, scope) : d;
    out.push({
      key: `piece:${piece.id}`,
      pathData: shift(piece.shapePathData),
    });
    for (const cut of piece.cutouts ?? []) {
      if (!isVisible(cut)) continue;
      out.push({ key: `cutout:${cut.id}`, pathData: shift(cut.pathData) });
    }
  }
  return out;
}

/** Look the geometry an axis ref addresses back up, in the same DISPLAY space
 *  the scan offered it in — resolved off the very list the scan used, so the
 *  two can't drift apart. */
function axisPathData(
  project: Project,
  partLayoutView: "spread" | "assembled",
  scope: paper.PaperScope,
  key: string,
): string | null {
  return (
    mirrorAxisOutlines(project, partLayoutView, scope).find(
      (o) => o.key === key,
    )?.pathData ?? null
  );
}

/** Shift an axis from display space into a part's ORIGINAL space. Only the
 *  origin moves — a translation leaves the direction alone. */
function axisInOriginalSpace(
  axis: AxisSpec,
  off: { dx: number; dy: number },
): AxisSpec {
  if (!off.dx && !off.dy) return axis;
  return {
    ...axis,
    origin: { x: axis.origin.x - off.dx, y: axis.origin.y - off.dy },
  };
}

/** True when the axis IS an outline of the object being mirrored — its own
 *  edge or corner. That is the exact case where the reflection lands flush
 *  against the original (the "fold" move), which is what the gap is for.
 *
 *  Deliberately narrow: mirroring across a DIFFERENT object's outline is how
 *  you butt a part up against that object, so a gap there would fight the
 *  user's intent, and a seam is never itself an axis, so it never qualifies. */
function axisIsSourceItself(
  sources: NonNullable<MirrorSelection>,
  key: string,
): boolean {
  if (sources.kind === "pieces") {
    return sources.pieceIds.some((id) => key === `piece:${id}`);
  }
  if (sources.kind === "cutout") return key === `cutout:${sources.cutoutId}`;
  return false;
}

/* `axisDistanceMm`, `selfAxisGap` and `GAP_REDRAW_EPS_MM` moved to
 * `src/model/mirrorAxis.ts` (#343 follow-up) when the construction Mirror
 * sub-tool needed the same self-axis gap. The gesture means the same thing in
 * both tools, so it must not be implemented twice — the same reason
 * `findAxisCandidate` and `nearestAxisFeature` already live there. */

/** Bounding-box centre of a pathData string, or null if it won't parse. */
function pathCenter(
  pathData: string,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  if (!pathData) return null;
  try {
    const cp = new scope.CompoundPath({ pathData, insert: false });
    const c = { x: cp.bounds.center.x, y: cp.bounds.center.y };
    cp.remove();
    return c;
  } catch {
    return null;
  }
}

export class LeatherMirrorController {
  /** Hover state: the axis under the cursor plus the ephemeral overlay items
   *  showing it and the reflection it would commit. Wiped on every refresh. */
  private hover: {
    ref: AxisRef;
    items: paper.Item[];
    /** The gap baked into the previewed geometry. The commit reuses THIS rather
     *  than recomputing, so what lands is exactly what was on screen. */
    gapMm: number;
  } | null = null;

  /** Notified whenever the gesture arms or disarms, so the ✓/✗ fly-out can
   *  show and hide with it. Fired only on that TRANSITION — the hover itself
   *  refreshes on every pointer move (the gap tracks the cursor), and syncing
   *  the fly-out that often would be pure DOM churn. */
  private onDraftChange: (() => void) | null = null;

  /** What armed the current hover (#40). A finger or stylus arms on PRESS and
   *  commits on RELEASE, so the ✓/✗ fly-out is unreachable for it — reaching
   *  the button means lifting, and lifting has already committed. Rather than
   *  offer a control that cannot be used, the draft reports empty for those
   *  pointers and the release is the confirm. */
  private armedBy: PointerKind = "mouse";

  constructor(private host: LeatherMirrorHost) {}

  setDraftChangeListener(fn: () => void): void {
    this.onDraftChange = fn;
  }

  /** 1 while a mirror is armed (an axis is resolved and previewed), else 0 —
   *  the "draft size" the shared fly-out builder gates visibility on. */
  get draftCount(): number {
    return this.hover && hasHover(this.armedBy) ? 1 : 0;
  }

  /** Fly-out ✓ / Enter: commit the armed mirror. Returns false when nothing is
   *  armed, so the caller can fall through to other key handling. */
  handleFinish(): boolean {
    if (!this.hover) return false;
    this.commitFromHover();
    return true;
  }

  /**
   * Fly-out ✗ / Escape: disarm.
   *
   * This clears the SELECTION as well as the overlay, which is what makes it a
   * real cancel: the preview is driven by hover, so merely dropping it would
   * bring the same mirror straight back on the next pointer move. Dropping the
   * source is also the way back out of the gesture now that pointing at open
   * space arms a mirror rather than clearing the selection.
   */
  handleDiscard(): boolean {
    const armed = this.hover !== null;
    const selected = mirrorSelection(this.host.getScene()) !== null;
    if (!armed && !selected) return false;
    this.clear();
    if (selected) {
      this.host.setSelection({ pieceId: null, decorationId: null });
    }
    return true;
  }

  /** Test hook: the axis currently under the cursor, or null. Exposed via
   *  `__editor.getLeatherMirrorAxis` so e2e can assert what the cursor
   *  resolves to without parsing overlay items. */
  getHoverAxis(): AxisRef | null {
    return this.hover?.ref ?? null;
  }

  /** Test hook: the union bounds of the dashed reflection preview, or null when
   *  nothing is hovered. Lets e2e assert that what's PREVIEWED is where the
   *  copy actually lands — the gap included — rather than trusting that the two
   *  code paths agree. Skips the axis highlight, which isn't part of the copy. */
  getPreviewBounds(): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null {
    if (!this.hover) return null;
    let box: paper.Rectangle | null = null;
    for (const item of this.hover.items) {
      if (!(item instanceof this.host.getScope().CompoundPath)) continue;
      box = box ? box.unite(item.bounds) : item.bounds;
    }
    return box
      ? { x: box.x, y: box.y, width: box.width, height: box.height }
      : null;
  }

  handleHover(p: { x: number; y: number }, kind?: PointerKind): void {
    this.armedBy = kind ?? "mouse";
    this.refresh(p);
  }

  /**
   * A press either COMMITS the hovered axis or PICKS the object to mirror.
   *
   * The two never collide, because an axis only exists within a few px of an
   * edge or corner AND only while something is already selected:
   *   - nothing selected → no axes are offered at all, so every click selects;
   *   - something selected → a click near an edge / corner commits the mirror
   *     (the highlight and dashed preview show that before the click lands),
   *     and a click anywhere else picks a new source.
   * So the whole gesture is usable from the canvas without visiting the Parts
   * list to select first.
   */
  handleDown(p: { x: number; y: number }, kind?: PointerKind): void {
    this.armedBy = kind ?? "mouse";
    // Refresh first: a touch / pen press arrives with no preceding hover, so
    // the overlay may not have been built for this position yet.
    this.refresh(p);
    // ...and for exactly that reason such a pointer does not COMMIT here (#40).
    // It has been shown nothing yet, so pressing would commit blind. The press
    // previews, the drag adjusts, and `handleUp` decides. Picking a new source
    // also waits for the release, so that one press cannot both preview an
    // axis and change what is being mirrored.
    if (!hasHover(kind)) return;
    if (this.hover) {
      this.commitFromHover();
      return;
    }
    this.selectAt(p);
  }

  /** onMove delegation: the preview follows a hover-less pointer through the
   *  drag. Returns true when it handled the move. */
  handleMove(p: { x: number; y: number }, kind?: PointerKind): boolean {
    if (hasHover(kind)) return false;
    this.armedBy = kind ?? "mouse";
    this.refresh(p);
    return true;
  }

  /** onUp delegation: the commit for a pointer that could not hover — or, with
   *  no axis under it, the source pick a mouse would have done on press.
   *  Returns true when it claimed the release. */
  handleUp(p: { x: number; y: number }, kind?: PointerKind): boolean {
    if (hasHover(kind)) return false;
    this.armedBy = kind ?? "mouse";
    this.refresh(p);
    if (this.hover) {
      this.commitFromHover();
      return true;
    }
    this.selectAt(p);
    return true;
  }

  /**
   * Pick the object under the cursor as the thing to mirror. Seams and cutouts
   * select as themselves; a hole / decoration / plain hit selects its owning
   * PART, since those aren't mirrorable on their own and the part is what the
   * user is pointing at anyway. An empty-canvas click clears the selection, as
   * it does with the Select tool.
   */
  private selectAt(p: { x: number; y: number }): void {
    const scope = this.host.getScope();
    const scene = this.host.getScene();
    const pick = pickLeatherObjectAt(scene, this.host.getProject(), scope, p, {
      includeStitch: true,
    });
    if (!pick) {
      const had = mirrorSelection(scene) !== null;
      this.host.setSelection({ pieceId: null, decorationId: null });
      // Only nudge when the click taught them nothing — they clicked bare mat
      // with nothing selected, so no axis lit up and nothing happened.
      if (!had) showCanvasToast(NO_SELECTION_HINT);
      return;
    }
    this.host.setSelection(selectionForPick(pick));
    // The selection just changed, so what the preview would reflect changed
    // too. Rebuild at the cursor: if the click landed near an edge of the very
    // object it selected, that edge lights up immediately as a usable axis.
    this.clear();
    this.refresh(p);
  }

  /**
   * "Mirror that way": with the cursor out in open space, treat the SELECTED
   * object's nearest edge or corner as the axis, so pointing past a side is
   * enough to say which way to reflect — no need to land on the edge itself.
   *
   * Corners count as well as edges, so aiming diagonally past one gives the
   * 180° point mirror — the copy goes where the aim points either way.
   *
   * Only engages when the cursor is clear of every visible part, which keeps it
   * from fighting the two things a click otherwise means: over another object a
   * click still SELECTS it (no axis, so no commit), and near any edge the exact
   * scan has already won before this is reached. Seams are excluded — a seam is
   * not an axis outline and has no inside to be outside of.
   */
  private aimedSelfEdge(
    project: Project,
    sources: NonNullable<MirrorSelection>,
    view: "spread" | "assembled",
    scope: paper.PaperScope,
    cursor: { x: number; y: number },
  ): AxisRef | null {
    if (sources.kind === "stitch") return null;
    if (this.pointOverAnyPart(project, view, scope, cursor)) return null;
    const all = mirrorAxisOutlines(project, view, scope);
    const ownKeys =
      sources.kind === "pieces"
        ? sources.pieceIds.map((id) => `piece:${id}`)
        : [`cutout:${sources.cutoutId}`];
    const own = all.filter((o) => ownKeys.includes(o.key));
    return nearestAxisFeature(own, cursor, scope);
  }

  /** True when the cursor is inside any visible part's outline (display space).
   *  A cutout void counts as inside its part — the part is still the thing the
   *  cursor is over. */
  private pointOverAnyPart(
    project: Project,
    view: "spread" | "assembled",
    scope: paper.PaperScope,
    cursor: { x: number; y: number },
  ): boolean {
    const pt = new scope.Point(cursor.x, cursor.y);
    for (const piece of project.pieces) {
      if (!isVisible(piece)) continue;
      const off = pieceDisplayOffset(piece, view);
      let cp: paper.CompoundPath | null = null;
      try {
        cp = new scope.CompoundPath({
          pathData: piece.shapePathData,
          insert: false,
        });
      } catch {
        continue;
      }
      const inside = cp.contains(pt.subtract(new scope.Point(off.dx, off.dy)));
      cp.remove();
      if (inside) return true;
    }
    return false;
  }

  /** Drop the overlay. Called on tool switch / pointer leave / cancel and
   *  immediately after a commit. */
  clear(): void {
    if (!this.hover) return;
    this.clearOverlay();
    this.onDraftChange?.();
  }

  /** Tear the overlay down WITHOUT notifying. `refresh` rebuilds in place, and
   *  a notify here would report "disarmed" mid-rebuild — the fly-out would hide
   *  and, since re-arming isn't a transition, never come back. */
  private clearOverlay(): void {
    if (!this.hover) return;
    for (const item of this.hover.items) item.remove();
    this.hover = null;
  }

  // -------------------------------------------------------------------
  // Preview
  // -------------------------------------------------------------------

  /** Recompute the hovered axis and redraw the highlight + reflected preview.
   *  Bails early when neither the axis NOR the gap has moved, so we don't
   *  thrash the overlay on every pointer move — but the gap tracks the cursor,
   *  so a plain slide away from the edge does have to redraw. */
  private refresh(cursor: { x: number; y: number }): void {
    const scene = this.host.getScene();
    const project = this.host.getProject();
    // Mirror is selection-driven: with nothing selected there is nothing to
    // reflect, so we don't offer axes either (which would let the user "pick"
    // an axis to no effect).
    const sources = mirrorSelection(scene);
    if (!sources) {
      this.clear();
      return;
    }
    const scope = this.host.getScope();
    const view = scene.getPartLayoutView();
    const ref =
      findAxisCandidate(
        mirrorAxisOutlines(project, view, scope),
        cursor,
        scope,
      ) ?? this.aimedSelfEdge(project, sources, view, scope, cursor);
    if (!ref) {
      this.clear();
      return;
    }
    const pathData = axisPathData(project, view, scope, ref.key);
    if (!pathData) {
      this.clear();
      return;
    }
    const axis = resolveAxisSpecFrom(pathData, ref, scope);
    if (!axis) {
      this.clear();
      return;
    }
    // Across the object's OWN outline the copy would land flush against it, so
    // it's pushed out by the cursor's distance from the axis — which puts its
    // leading edge under the cursor (see `axisDistanceMm`). Across anything
    // else there's no gap: butting the copy against that object is the point.
    const gapMm = axisIsSourceItself(sources, ref.key)
      ? axisDistanceMm(axis, cursor)
      : 0;
    const cur = this.hover;
    if (
      cur &&
      cur.ref.key === ref.key &&
      cur.ref.kind === ref.kind &&
      cur.ref.pathIndex === ref.pathIndex &&
      cur.ref.segmentIndex === ref.segmentIndex &&
      Math.abs(cur.gapMm - gapMm) < GAP_REDRAW_EPS_MM
    ) {
      return;
    }
    const armedBefore = this.hover !== null;
    this.clearOverlay();

    const items: paper.Item[] = [];
    const zoom = scope.view.zoom > 0 ? scope.view.zoom : 1;
    const stroke = new scope.Color(PREVIEW_STROKE());
    // Axis highlight: a ring on the pivot for a point mirror, a thick stroke
    // along the segment for a reflection.
    if (axis.kind === "point") {
      const ring = new scope.Path.Circle(
        new scope.Point(axis.origin.x, axis.origin.y),
        6 / zoom,
      );
      ring.strokeColor = stroke;
      ring.strokeWidth = 2;
      ring.strokeScaling = false;
      ring.fillColor = null;
      ring.data = { ephemeral: true };
      items.push(ring);
    } else if (axis.dir) {
      const seg = new scope.Path.Line(
        new scope.Point(axis.origin.x, axis.origin.y),
        new scope.Point(axis.origin.x + axis.dir.x, axis.origin.y + axis.dir.y),
      );
      seg.strokeColor = stroke;
      seg.strokeWidth = 3;
      seg.strokeScaling = false;
      seg.data = { ephemeral: true };
      items.push(seg);
    }

    // Reflected outlines — what a click would land, including the self-axis gap,
    // so the dashed preview IS the result rather than an approximation of it.
    // Previewing every source's geometry (a part's cutouts and seams included)
    // is what makes the gesture legible.
    const matrix = buildMirrorMatrix(axis);
    for (const group of this.previewGroups(project, sources, view, scope)) {
      // Fold the gap into the reflection's translation rather than moving each
      // path afterwards — same result, one transform instead of two.
      const g =
        gapMm > 0 && group.gapRef
          ? selfAxisGap(axis, group.gapRef, gapMm)
          : { dx: 0, dy: 0 };
      const m = { ...matrix, tx: matrix.tx + g.dx, ty: matrix.ty + g.dy };
      for (const d of group.paths) {
        const reflected = mirrorPathData(d, m, scope);
        if (!reflected) continue;
        try {
          const item = new scope.CompoundPath({
            pathData: reflected,
            insert: true,
          });
          item.strokeColor = stroke;
          item.strokeWidth = 1.6;
          item.strokeScaling = false;
          item.dashArray = [4, 3];
          item.fillColor = null;
          item.data = { ephemeral: true };
          items.push(item);
        } catch {
          /* preview only — a path we can't parse just doesn't draw */
        }
      }
    }
    this.hover = { ref, items, gapMm };
    // Only a real arm/disarm transition is worth a fly-out sync.
    if (!armedBefore) this.onDraftChange?.();
  }

  /**
   * The geometry the preview reflects, grouped ONE GROUP PER SOURCE OBJECT so
   * each can take its own gap offset (the direction depends on where that
   * object sits relative to the axis).
   *
   * Paths come out in DISPLAY space — shifted by their owner's cut-layout
   * offset (#786) — because that's the space the axis is in and the space the
   * result is drawn in. That holds for child features too: a mirrored cutout is
   * stored in the part's original space but rendered through the same offset,
   * so shifting here previews exactly where it will appear.
   *
   * `gapRef` is the point whose reflection sets the gap direction, and it
   * matches what the commit uses: a part's OUTLINE centre (not the bbox of the
   * part plus its seams), and a cutout's own centre.
   */
  private previewGroups(
    project: Project,
    sources: NonNullable<MirrorSelection>,
    view: "spread" | "assembled",
    scope: paper.PaperScope,
  ): Array<{ paths: string[]; gapRef: { x: number; y: number } | null }> {
    const groups: Array<{
      paths: string[];
      gapRef: { x: number; y: number } | null;
    }> = [];
    const shifted = (d: string, off: { dx: number; dy: number }) =>
      off.dx || off.dy ? translatePathData(d, off.dx, off.dy, scope) : d;

    if (sources.kind === "pieces") {
      for (const id of sources.pieceIds) {
        const piece = project.pieces.find((p) => p.id === id);
        if (!piece) continue;
        const off = pieceDisplayOffset(piece, view);
        const shape = shifted(piece.shapePathData, off);
        const paths = [shape];
        for (const cut of piece.cutouts ?? []) {
          paths.push(shifted(cut.pathData, off));
        }
        for (const s of pieceStitchLines(piece)) {
          const d = seamPathData(s);
          if (d) paths.push(shifted(d, off));
        }
        groups.push({ paths, gapRef: pathCenter(shape, scope) });
      }
      return groups;
    }
    if (sources.kind === "cutout") {
      const piece = project.pieces.find((p) => p.id === sources.pieceId);
      const cut = (piece?.cutouts ?? []).find((c) => c.id === sources.cutoutId);
      if (piece && cut) {
        const d = shifted(cut.pathData, pieceDisplayOffset(piece, view));
        groups.push({ paths: [d], gapRef: pathCenter(d, scope) });
      }
      return groups;
    }
    for (const entry of sources.entries) {
      const piece = project.pieces.find((p) => p.id === entry.pieceId);
      const seam = piece
        ? pieceStitchLines(piece).find((s) => s.id === entry.stitchId)
        : null;
      if (!piece || !seam) continue;
      const d = seamPathData(seam);
      if (!d) continue;
      // A seam is never itself an axis, so it never takes a gap.
      groups.push({
        paths: [shifted(d, pieceDisplayOffset(piece, view))],
        gapRef: null,
      });
    }
    return groups;
  }

  // -------------------------------------------------------------------
  // Commit
  // -------------------------------------------------------------------

  private commitFromHover(): void {
    const hover = this.hover;
    if (!hover) return;
    const scope = this.host.getScope();
    const scene = this.host.getScene();
    const sources = mirrorSelection(scene);
    if (!sources) return;
    const project = this.host.getProject();
    const view = scene.getPartLayoutView();
    const pathData = axisPathData(project, view, scope, hover.ref.key);
    if (!pathData) return;
    // Display space, matching what the scan offered and the preview drew.
    const axis = resolveAxisSpecFrom(pathData, hover.ref, scope);
    if (!axis) return;
    // Reuse the gap the PREVIEW was drawn with rather than recomputing from the
    // cursor: `handleDown` refreshes before committing, so this is current, and
    // taking the same number guarantees the part lands exactly where the dashed
    // outline showed it.
    const gapMm = hover.gapMm;

    if (sources.kind === "pieces") {
      const newIds: string[] = [];
      this.host.commit("Mirror part", (proj) => {
        let next = proj;
        // Walk project order so a multi-select run has a stable draw order
        // regardless of the click order (as Duplicate does).
        for (const src of proj.pieces) {
          if (!sources.pieceIds.includes(src.id)) continue;
          // A construction-LINKED part is copied as an unlinked, frozen
          // snapshot of what's DISPLAYED — `clonePieceForDuplicate` drops the
          // link, so the geometry has to be moved to where the original is
          // drawn before reflecting, or the copy would mirror the un-laid-out
          // geometry instead of the part the user is looking at (#907).
          const off = pieceDisplayOffset(src, view);
          const clone: Piece = translatePiece(
            clonePieceForDuplicate(src, nextPartName(next)),
            off.dx,
            off.dy,
            scope,
          );
          let mirrored = mirrorPieceAcross(clone, axis, scope);
          if (gapMm > 0) {
            const c = pathCenter(clone.shapePathData, scope);
            if (c) {
              const g = selfAxisGap(axis, c, gapMm);
              mirrored = translatePiece(mirrored, g.dx, g.dy, scope);
            }
          }
          newIds.push(mirrored.id);
          next = { ...next, pieces: [...next.pieces, mirrored] };
        }
        return next;
      });
      if (newIds.length > 0) {
        this.host.setSelection({
          pieceId: newIds[newIds.length - 1] ?? null,
          pieceIds: newIds,
          decorationId: null,
          holeId: null,
          cutoutId: null,
          stitchId: null,
          constructionIds: [],
          constructionGroupIds: [],
          tracingImageIds: [],
        });
      }
    } else if (sources.kind === "cutout") {
      let newCutoutId: string | null = null;
      this.host.commit("Mirror cutout", (proj) => {
        const owner = proj.pieces.find((p) => p.id === sources.pieceId);
        const src = owner?.cutouts.find((c) => c.id === sources.cutoutId);
        if (!owner || !src) return proj;
        // The copy stays INSIDE this part, so it must end up in the part's
        // original space — take the axis back there rather than moving the
        // geometry out and back (#786).
        const local = axisInOriginalSpace(
          axis,
          pieceDisplayOffset(owner, view),
        );
        let clone = mirrorCutoutAcross(
          cloneCutoutForDuplicate(src, 0, 0, scope, nextCutoutName(owner)),
          local,
          scope,
        );
        if (gapMm > 0) {
          const c = pathCenter(src.pathData, scope);
          if (c) {
            const g = selfAxisGap(local, c, gapMm);
            clone = {
              ...clone,
              pathData: translatePathData(clone.pathData, g.dx, g.dy, scope),
            };
          }
        }
        newCutoutId = clone.id;
        return mapPiece(proj, owner.id, (pc) => ({
          ...pc,
          cutouts: [...pc.cutouts, clone],
        }));
      });
      if (newCutoutId) {
        this.host.setSelection({
          pieceId: sources.pieceId,
          decorationId: null,
          holeId: null,
          stitchId: null,
          cutoutId: newCutoutId,
        });
      }
    } else {
      const cloned: Array<{ ownerId: string; id: string }> = [];
      this.host.commit("Mirror stitch line", (proj) => {
        let next = proj;
        for (const entry of sources.entries) {
          const owner = next.pieces.find((p) => p.id === entry.pieceId);
          const src = owner
            ? pieceStitchLines(owner).find((s) => s.id === entry.stitchId)
            : null;
          if (!owner || !src) continue;
          // As with cutouts: the copy lives on this part, so mirror in the
          // part's own space rather than in display space.
          const local = axisInOriginalSpace(
            axis,
            pieceDisplayOffset(owner, view),
          );
          const clone = mirrorStitchLineAcross(
            cloneStitchForDuplicate(src, 0, 0, nextStitchName(next)),
            local,
            scope,
          );
          cloned.push({ ownerId: owner.id, id: clone.id });
          next = mapPiece(next, owner.id, (pc) => ({
            ...pc,
            stitchLines: [...pieceStitchLines(pc), clone],
          }));
        }
        return next;
      });
      if (cloned.length > 0) {
        const ids = cloned.map((c) => c.id);
        this.host.setSelection({
          pieceId: cloned[cloned.length - 1]!.ownerId,
          decorationId: null,
          holeId: null,
          cutoutId: null,
          stitchId: ids[ids.length - 1] ?? null,
          stitchIds: ids,
        });
      }
    }
    // The overlay is stale now that the model changed — clearing it forces a
    // fresh one on the next move.
    this.clear();
    // Hand the user to Select, with the copy selected (#40).
    //
    // This tool used to STAY in Mirror so more axes could be fired at, the
    // click-click pattern the construction sub-tool uses. That pattern needs
    // the source selection to stay put to be useful, and this tool moves it to
    // the new copy — so a second press on the same axis mirrored the COPY back
    // across it, landing an invisible duplicate exactly on top of the
    // original. Staying armed on a selection that had moved was the trap;
    // leaving the mode closes it, and lands you on the tool that can drag the
    // thing you just made. The construction sub-tool keeps its chaining, which
    // is why this is here and not in `mirrorController`.
    this.host.activateTool("select");
  }
}
